import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from 'Utils/Hooks/useAuth';
import { api } from './api';
import { getServer, getServerKey, subscribeServer } from './servers';

/** Current server key; components re-render when it is switched. */
export function useServerKey() {
  return useSyncExternalStore(subscribeServer, getServerKey);
}

export function useServer() {
  useServerKey();
  return getServer();
}

/** 'admin' | 'partner' plus the API prefix for that role. */
export function useRole() {
  const { currentAdmin } = useAuth();
  const isAdmin = currentAdmin?.user_type === 'ADMIN';
  return {
    user: currentAdmin,
    isAdmin,
    role: isAdmin ? 'admin' : 'partner',
    prefix: isAdmin ? '/admin' : '/partner',
    home: isAdmin ? '/dashboard' : '/partner',
  };
}

/**
 * Fetches `path` with `params` and refetches when either (or the server)
 * changes. Stale responses are ignored. Pass path = null to skip.
 */
export function useApi(path, params, { enabled = true, keepPrevious = true } = {}) {
  const server = useServerKey();
  const key = JSON.stringify([path, params, server]);
  const [state, setState] = useState({ data: undefined, error: null, loading: !!(path && enabled) });
  const [tick, setTick] = useState(0);
  const latest = useRef(0);

  useEffect(() => {
    if (!path || !enabled) {
      setState((s) => ({ ...s, loading: false }));
      return undefined;
    }
    const id = ++latest.current;
    const controller = new AbortController();
    setState((s) => ({ data: keepPrevious ? s.data : undefined, error: null, loading: true }));

    api
      .get(path, params, { signal: controller.signal })
      .then((res) => {
        if (id === latest.current) setState({ data: res.data, error: null, loading: false, message: res.message });
      })
      .catch((error) => {
        if (error.name === 'AbortError' || id !== latest.current) return;
        setState((s) => ({ data: keepPrevious ? s.data : undefined, error, loading: false }));
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, refetch };
}

/** Partners (admin only) for filter dropdowns. Cached for the session. */
let partnersCache = null;
export function usePartners(enabled = true) {
  const [partners, setPartners] = useState(partnersCache || []);
  const [loading, setLoading] = useState(enabled && !partnersCache);

  useEffect(() => {
    if (!enabled || partnersCache) return;
    let live = true;
    api
      .get('/admin/user')
      .then((res) => {
        partnersCache = (res.data || [])
          .filter((u) => u.user_type !== 'ADMIN')
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        if (live) setPartners(partnersCache);
      })
      .catch(() => {})
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [enabled]);

  return { partners, loading };
}

/** Products visible to the user; for admins optionally narrowed to one partner. */
export function useProducts({ isAdmin, partnerId } = {}) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const req = isAdmin ? api.get('/admin/product/', partnerId ? { user_id: partnerId } : undefined) : api.get('/product/');
    req
      .then((res) => live && setProducts((res.data || []).slice().sort((a, b) => String(a.name).localeCompare(String(b.name)))))
      .catch(() => live && setProducts([]))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [isAdmin, partnerId]);

  return { products, loading };
}

/** Keeps filter state in the URL so views can be bookmarked and shared. */
export function useUrlState(defaults) {
  const read = () => {
    const sp = new URLSearchParams(window.location.search);
    const out = { ...defaults };
    Object.keys(defaults).forEach((k) => {
      if (sp.has(k)) out[k] = sp.get(k);
    });
    return out;
  };
  const [state, setState] = useState(read);

  const update = useCallback((patch) => {
    setState((prev) => {
      const next = { ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) };
      const sp = new URLSearchParams(window.location.search);
      Object.keys(defaults).forEach((k) => {
        const v = next[k];
        if (v === undefined || v === null || v === '' || v === defaults[k]) sp.delete(k);
        else sp.set(k, v);
      });
      const qs = sp.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [state, update];
}

export function useMediaQuery(query) {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const fn = () => setMatches(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', fn) : mq.addListener(fn);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', fn) : mq.removeListener(fn));
  }, [query]);
  return matches;
}

export function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
